/**
 * The colour a model is rendered in.
 *
 * Pure, and in the domain layer, because a model's colour is a property of the stored
 * model rather than of the renderer — a backend that owned the library would have to
 * agree on the same representation and the same default.
 *
 * Colours are hex strings in `#rrggbb`, which is the one form that three.js, a CSS
 * `background-color`, and an `<input type="color">` all accept unchanged. Anything else
 * would need converting at three separate boundaries.
 */

/**
 * What a model is rendered in when nothing else has been chosen.
 *
 * A neutral grey: it reads as unpainted rather than as a decision, and it shows form
 * better than a saturated colour, which is what you want when the point is to judge a
 * shape.
 */
export const DEFAULT_COLOUR = '#9aa7b4';

export interface NamedColour {
  readonly name: string;
  readonly hex: string;
}

/**
 * The short list offered before reaching for the full picker.
 *
 * Chosen to look like filament rather than like a paint chart, and to stay distinct from
 * one another under the scene's lighting. "Black" is a very dark grey on purpose: true
 * black takes no shading, so a black model reads as a silhouette with no discernible
 * shape — which defeats the point of looking at it.
 */
export const STANDARD_COLOURS: readonly NamedColour[] = [
  { name: 'Grey', hex: DEFAULT_COLOUR },
  { name: 'White', hex: '#e8ecef' },
  { name: 'Black', hex: '#2a2e33' },
  { name: 'Red', hex: '#d03a34' },
  { name: 'Orange', hex: '#e07b28' },
  { name: 'Yellow', hex: '#e3c02a' },
  { name: 'Green', hex: '#3f9e54' },
  { name: 'Blue', hex: '#3a7bd0' },
  { name: 'Purple', hex: '#8a5cc4' },
  { name: 'Pink', hex: '#d76a9c' },
];

const SHORTHAND = /^#[0-9a-f]{3}$/;
const FULL = /^#[0-9a-f]{6}$/;

/**
 * Bring a colour to canonical `#rrggbb`, or reject it.
 *
 * Applied to anything read back from storage as well as to anything typed in. A stored
 * record is not necessarily one this version of the code wrote — it may predate a change,
 * or have been edited by hand in devtools — and passing an unvalidated string straight to
 * a CSS property or a shader is how a bad value becomes an invisible model rather than an
 * error.
 */
export function normaliseColour(value: string): string | undefined {
  const trimmed = value.trim().toLowerCase();

  if (FULL.test(trimmed)) return trimmed;

  if (SHORTHAND.test(trimmed)) {
    // `#abc` means `#aabbcc`. Browsers accept the short form; three.js and an
    // `<input type="color">` do not, so it is expanded once here rather than guarded
    // against at each of them.
    const [, r, g, b] = trimmed;
    return `#${r ?? ''}${r ?? ''}${g ?? ''}${g ?? ''}${b ?? ''}${b ?? ''}`;
  }

  return undefined;
}

/**
 * The colour to render a model in: its own, if it has a valid one, or the default.
 *
 * Taking a bare shape rather than a whole `ModelMeta` keeps this usable from the picker,
 * which deals in a colour before there is a record to put it in.
 *
 * The parameter says `string | undefined` as well as being optional, unlike `ModelMeta`
 * where the field is merely optional. That is not redundancy: a stored record either has
 * the key or does not, but a *caller* may well be holding a `string | undefined` it got
 * from somewhere else, and this handles that case anyway.
 */
export function colourOf(model: { readonly colour?: string | undefined }): string {
  const stored = model.colour;
  return (stored === undefined ? undefined : normaliseColour(stored)) ?? DEFAULT_COLOUR;
}

/**
 * A colour as hue, saturation and lightness — the form a slider-based picker works in.
 *
 * Hue is degrees around the wheel, `0` to `360`; saturation and lightness are percentages.
 * These are the ranges an `<input type="range">` is given directly, so keeping them here
 * means the UI does no arithmetic of its own.
 */
export interface Hsl {
  /** Degrees, 0–360. */
  readonly hue: number;
  /** Percent, 0–100. */
  readonly saturation: number;
  /** Percent, 0–100. */
  readonly lightness: number;
}

/**
 * Split a colour into hue, saturation and lightness.
 *
 * An unusable value gives the default's components rather than throwing, matching what
 * every other function here does with one: the picker has to show *something*, and the
 * thing it should show is what will actually be rendered.
 */
export function hexToHsl(hex: string): Hsl {
  const normalised = normaliseColour(hex) ?? DEFAULT_COLOUR;

  const red = channel(normalised, 1);
  const green = channel(normalised, 3);
  const blue = channel(normalised, 5);

  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const lightness = (max + min) / 2;
  const chroma = max - min;

  // A grey has no hue to speak of. Reporting 0 rather than something arbitrary keeps the
  // hue slider still while the saturation slider is dragged up from zero, which is what
  // makes that gesture usable.
  if (chroma === 0) {
    return { hue: 0, saturation: 0, lightness: percent(lightness) };
  }

  const saturation = chroma / (1 - Math.abs(2 * lightness - 1));

  let hue: number;
  if (max === red) {
    hue = ((green - blue) / chroma) % 6;
  } else if (max === green) {
    hue = (blue - red) / chroma + 2;
  } else {
    hue = (red - green) / chroma + 4;
  }

  hue *= 60;
  if (hue < 0) hue += 360;

  return {
    hue: Math.round(hue) % 360,
    saturation: percent(saturation),
    lightness: percent(lightness),
  };
}

/** Build a colour from hue, saturation and lightness. Inputs outside range are clamped. */
export function hslToHex({ hue, saturation, lightness }: Hsl): string {
  // Hue wraps rather than clamping: a slider at 360 and one at 0 are the same red, and
  // treating them differently would put a seam in the middle of the spectrum.
  const h = ((hue % 360) + 360) % 360;
  const s = clamp(saturation, 0, 100) / 100;
  const l = clamp(lightness, 0, 100) / 100;

  const chroma = (1 - Math.abs(2 * l - 1)) * s;
  const second = chroma * (1 - Math.abs(((h / 60) % 2) - 1));
  const offset = l - chroma / 2;

  const [red, green, blue] = sector(h, chroma, second);

  return `#${byte(red + offset)}${byte(green + offset)}${byte(blue + offset)}`;
}

/** Which of the six 60-degree arcs of the wheel the hue falls in. */
function sector(hue: number, chroma: number, second: number): [number, number, number] {
  if (hue < 60) return [chroma, second, 0];
  if (hue < 120) return [second, chroma, 0];
  if (hue < 180) return [0, chroma, second];
  if (hue < 240) return [0, second, chroma];
  if (hue < 300) return [second, 0, chroma];
  return [chroma, 0, second];
}

/** One channel of a canonical `#rrggbb`, as 0–1. */
function channel(hex: string, at: number): number {
  return Number.parseInt(hex.slice(at, at + 2), 16) / 255;
}

function byte(value: number): string {
  return Math.round(clamp(value, 0, 1) * 255)
    .toString(16)
    .padStart(2, '0');
}

function percent(value: number): number {
  return Math.round(clamp(value, 0, 1) * 100);
}

function clamp(value: number, low: number, high: number): number {
  if (!Number.isFinite(value)) return low;
  return Math.min(Math.max(value, low), high);
}

/**
 * A name for a colour, for anything that has to be read rather than looked at.
 *
 * Standard colours get their name; anything from the full picker gets its hex code, which
 * is not friendly but is at least exact — and is what somebody trying to reproduce the
 * colour would actually want.
 */
export function describeColour(hex: string): string {
  const normalised = normaliseColour(hex) ?? DEFAULT_COLOUR;
  const standard = STANDARD_COLOURS.find((colour) => colour.hex === normalised);
  return standard?.name ?? normalised.toUpperCase();
}
